import { normalizeSubdomain } from './aiRecommendationAccess';

/**
 * Explicit allowlist for Settings onboarding reset (Ashwini / Saba demo orgs).
 * Keep in sync with infrastructure/lambda/shared/src/onboarding-reset-access.ts
 */
export const ONBOARDING_RESET_ALLOWLIST_SUBDOMAINS = [
  'compedu-dev-demo2',
  'compedu-product-demo2',
  'complianceeducation-demo',
  'globalcompliancesolutions',
  'onboarding-testing',
  'test-onboarding',
  'test-branding',
] as const;

/** @deprecated Use ONBOARDING_RESET_ALLOWLIST_SUBDOMAINS */
export const ONBOARDING_RESET_BUILTIN_SUBDOMAINS = ONBOARDING_RESET_ALLOWLIST_SUBDOMAINS;

function isAllowlistedOnboardingResetSubdomain(value: string | null): boolean {
  return (
    value != null &&
    (ONBOARDING_RESET_ALLOWLIST_SUBDOMAINS as readonly string[]).includes(value)
  );
}

function getTenantSubdomainFromHostname(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  const firstSegment = hostname.split('.')[0];
  return firstSegment || undefined;
}

/**
 * Resolve tenant subdomain for onboarding-reset UI gating.
 * Deployed: hostname first segment. Local: org profile subdomain, then VITE_TENANT_SUBDOMAIN.
 */
export function resolveOnboardingResetTenantSubdomain(
  organizationSubdomain?: string | null,
): string | undefined {
  const fromHostname = getTenantSubdomainFromHostname();
  if (fromHostname) {
    return fromHostname;
  }

  const fromOrganization = normalizeSubdomain(organizationSubdomain);
  if (fromOrganization) {
    return fromOrganization;
  }

  const fromEnv = normalizeSubdomain(import.meta.env.VITE_TENANT_SUBDOMAIN);
  if (fromEnv) {
    return fromEnv;
  }

  return undefined;
}

/**
 * Only verified tenant subdomains on the explicit allowlist may reset onboarding.
 * Organization display names are not used for authorization.
 */
export function isOnboardingResetOrganization(
  organizationSubdomain?: string | null,
): boolean {
  return isAllowlistedOnboardingResetSubdomain(normalizeSubdomain(organizationSubdomain));
}

/**
 * Frontend Settings demo-tools visibility. Deployed hosts must match the allowlist.
 * On localhost, show tools for local QA; DELETE /settings reset stays allowlisted on the API.
 */
export function canAccessOnboardingResetFromSubdomain(
  organizationSubdomain?: string | null,
  isLocal = false,
): boolean {
  const tenantSubdomain = resolveOnboardingResetTenantSubdomain(organizationSubdomain);
  if (isOnboardingResetOrganization(tenantSubdomain)) {
    return true;
  }
  return isLocal;
}
